import { Timezone } from '../../../NonReact/Types/Types'
import {
    CACHE_TIME,
    getTimezoneDataURL,
    mappedTimezoneURL,
    TIMEZONE_DEFAULT_URL
} from '../../../NonReact/constants/ApiConstants'
import { SelectOptions } from '../Components/Selects'
import { SelectOption } from '../Components/Selects/Types'
import { requestGetJSON } from '../../../NonReact/common/API/REST/RESTAPIClient'
import { DEFAULT_TIMEZONE } from '../../TelSysConfiguration/Constants/Constants'
import { $error } from '../Utils/ConsoleUtils'
import { JSONResponse } from '../../../NonReact/common/Types/Types'

/**
 * This function fetches timezone data from a specified URL and transforms the response into a format suitable for a select dropdown.
 * It returns a promise that resolves to an array of select options, each with a label and a value corresponding to the timezone's description and ID, respectively.
 * If the response is null or undefined, it returns an empty array.
 */
export const getTimezoneOptions = (): Promise<SelectOptions<string>> =>
    requestGetJSON<Timezone[]>(getTimezoneDataURL).then(
        (timezoneDatas: JSONResponse<Timezone[]>): SelectOptions<string> =>
            timezoneDatas?.map(
                (timezoneData: Timezone): SelectOption<string> => ({
                    label: timezoneData.description,
                    value: timezoneData.timeZoneID
                })
            ) ?? []
    )

/**
 * This function fetches the default timezone for the server.
 */
export const getTimezoneDefault = (): Promise<string> =>
    requestGetJSON<string>(TIMEZONE_DEFAULT_URL, {
        cacheTime: CACHE_TIME,
        isStringResult: true
    })
        .then((defaultTimezone: Nullable<string>): string => defaultTimezone ?? DEFAULT_TIMEZONE)
        .catch((e: Error): string => {
            $error(e)
            return DEFAULT_TIMEZONE
        })

/**
 * This function fetches the mapped timezone for the server.
 * @param timezone
 * @returns Promise<string> - The mapped timezone for the server is returned as a string value.
 */
export const getMappedTimezone = (timezone: string): Promise<string> =>
    requestGetJSON<string>(mappedTimezoneURL(timezone), {
        cacheTime: CACHE_TIME,
        isStringResult: true
    })
        .then((mappedTimezone: Nullable<string>): string => mappedTimezone ?? DEFAULT_TIMEZONE)
        .catch((e: Error): string => {
            $error(e)
            return DEFAULT_TIMEZONE
        })
